import { tolerances, } from "Qrc/lib/constants";
import { GeometryEntityType, SegmentType, } from "Qrc/lib/generated/enum";
import { isStringIndexedInterface, } from "Qrc/lib/generated/typeguard";
export function isArray(arr, isT) {
    if (!Array.isArray(arr)) {
        return false;
    }
    return arr.find(element => !isT(element)) === undefined;
}
export function isString(obj) {
    return typeof obj === "string";
}
export function isNumber(obj) {
    return typeof obj === "number";
}
export function isBoolean(obj) {
    return typeof obj === "boolean";
}
export function isObject(obj) {
    return typeof obj === "object";
}
export function hasPropertyT(obj, property, isT) {
    return isT(obj[property]);
}
export function hasOptionalPropertyT(obj, property, isT) {
    return hasPropertyT(obj, property, (t) => t === undefined || isT(t));
}
function isEqualArray(first, second) {
    if (first.length !== second.length) {
        return false;
    }
    for (const index in first) {
        if (!isEqual(first[index], second[index])) {
            return false;
        }
    }
    return true;
}
export function getKeysOfObject(obj) {
    const keys = Object.keys(obj);
    return keys;
}
function compareStrings(lhs, rhs) {
    if (lhs < rhs) {
        return -1;
    }
    else if (rhs < lhs) {
        return 1;
    }
    else {
        return 0;
    }
}
export function isEqual(first, second) {
    if (typeof first !== typeof second) {
        return false;
    }
    if (first === undefined) {
        return true;
    }
    if (first === null) {
        return second === null;
    }
    if (second === null) {
        return first === null;
    }
    if (isNumber(first) || isBoolean(first) || isString(first)) {
        return first === second;
    }
    if (!isObject(first)) {
        return wsi4.throwError("first: wrong type");
    }
    if (!isObject(second)) {
        return wsi4.throwError("second: wrong type");
    }
    if (Array.isArray(first)) {
        if (!Array.isArray(second)) {
            return wsi4.throwError("second: not array");
        }
        return isEqualArray(first, second);
    }
    const propertiesMatchKeys = (obj) => isEqualArray(Object.keys(obj)
        .sort(compareStrings), Object.getOwnPropertyNames(obj)
        .sort(compareStrings));
    if (!propertiesMatchKeys(first) || !propertiesMatchKeys(second)) {
        return wsi4.throwError("Object invalid");
    }
    const isValidPrototype = (obj) => JSON.stringify(Object.getPrototypeOf(obj)) === "{}";
    if (!isValidPrototype(first) || !isValidPrototype(second)) {
        return wsi4.throwError("Object prototype invalid");
    }
    const emptyKeysMeansEmptyObject = (obj) => (Object.keys(obj).length === 0) === (JSON.stringify(obj) === "{}");
    if (!emptyKeysMeansEmptyObject(first) || !emptyKeysMeansEmptyObject(second)) {
        return wsi4.throwError("Object invalid");
    }
    const keys = getKeysOfObject(first);
    if (keys.length !== Object.keys(second).length) {
        return false;
    }
    return keys.every(key => isEqual(first[key], second[key]));
}
export function parseJson(input, isT) {
    try {
        const value = JSON.parse(input);
        return isT(value) ? value : undefined;
    }
    catch (e) {
        if (!(e instanceof SyntaxError)) {
            throw e;
        }
        return undefined;
    }
}
export function parseJsonDefault(input, defaultValue, isT) {
    const p = parseJson(input, isT);
    return p === undefined ? defaultValue : p;
}
export function readSetting(key, defaultValue, isT) {
    const json = wsi4.io.settings.read(key);
    return json === undefined ? defaultValue : parseJsonDefault(json, defaultValue, isT);
}
export function writeSetting(key, value) {
    wsi4.io.settings.write(key, JSON.stringify(value));
}
export function isNonNegativeNumber(n) {
    if (!Number.isFinite(n)) {
        wsi4.util.warn("validNonNegative(): expecting finite number; got " + String(n));
        return false;
    }
    if (n < 0) {
        wsi4.util.warn("validNonNegative(): expecting non-negative number");
        return false;
    }
    return true;
}
export function getRowForThreshold(tableData, cond) {
    if (tableData.length === 0) {
        wsi4.util.warn("getRowForThreshold(): tableData data empty");
        return undefined;
    }
    if (!cond(tableData[tableData.length - 1])) {
        wsi4.util.warn("getRowForThreshold(): threshold exceeds largest tableData value. Using largest tableData value.");
        return tableData[tableData.length - 1];
    }
    return tableData.find(cond);
}
function bbDimensionN(boundingBox, dim) {
    return boundingBox.upper.entries[dim] - boundingBox.lower.entries[dim];
}
export function bbDimensionX(boundingBox) {
    return bbDimensionN(boundingBox, 0);
}
export function bbDimensionY(boundingBox) {
    return bbDimensionN(boundingBox, 1);
}
export function bbDimensionZ(boundingBox) {
    return bbDimensionN(boundingBox, 2);
}
export function createAssemblyMap(assembly, globalParentCosys) {
    const recursiveAddToArray = (array, asm, parentAsm) => {
        const id = wsi4.util.toKey(asm);
        if (array.find(element => element.id === id) !== undefined) {
            return;
        }
        const wcs = (() => {
            const childCosys = wsi4.geo.assembly.worldCoordinateSystem(asm);
            if (globalParentCosys === undefined) {
                return childCosys;
            }
            else {
                return wsi4.geo.util.applyCoordinateSystem(globalParentCosys, childCosys);
            }
        })();
        const wcsTransformation = wsi4.geo.util.toProjectiveTransformationMatrix(wcs);
        const subAssemblies = wsi4.geo.assembly.subAssemblies(asm);
        const subAssemblyIds = subAssemblies.map((subAsm) => wsi4.util.toKey(subAsm));
        const brep = wsi4.geo.assembly.brep(asm);
        array.push({
            id: id,
            assembly: {
                wcsTransformation: wcsTransformation.entries,
                parentId: parentAsm === undefined ? undefined : wsi4.util.toKey(parentAsm),
                brepId: brep === undefined ? undefined : wsi4.util.toKey(brep),
                subAssemblyIds: subAssemblyIds,
            },
        });
        for (const subAsm of subAssemblies) {
            recursiveAddToArray(array, subAsm, asm);
        }
    };
    const result = [];
    recursiveAddToArray(result, assembly);
    return result;
}
export function createBrepMapFromAssembly(assembly) {
    const recursiveAddToMap = (array, assembly) => {
        for (const subAssembly of wsi4.geo.assembly.subAssemblies(assembly)) {
            recursiveAddToMap(array, subAssembly);
        }
        const brep = wsi4.geo.assembly.brep(assembly);
        if (brep === undefined) {
            return;
        }
        if (array.find(element => element.id === wsi4.util.toKey(brep)) !== undefined) {
            return;
        }
        array.push({
            id: wsi4.util.toKey(brep),
            brepJson: wsi4.geo.brep.toPolyJson(brep),
        });
    };
    const result = [];
    recursiveAddToMap(result, assembly);
    return result;
}
export function rootIdToVertex(rootId) {
    const id = wsi4.node.vertexFromRootId(rootId);
    if (id === undefined) {
        return wsi4.throwError("Cannot get Vertex for root id " + JSON.stringify(rootId));
    }
    return id;
}
export function computeInnerOuterContourLengths(twoDimRep) {
    const iops = wsi4.cam.util.extractInnerOuterPolygons(twoDimRep);
    const innerLength = iops.reduce((acc0, iop) => {
        const ips = wsi4.geo.util.innerPolygons(iop);
        return ips.reduce((acc1, ip) => acc1 + wsi4.geo.util.circumference(ip), acc0);
    }, 0);
    const outerLength = iops.reduce((acc, iop) => {
        const op = wsi4.geo.util.outerPolygon(iop);
        return acc + wsi4.geo.util.circumference(op);
    }, 0);
    return [
        innerLength,
        outerLength,
    ];
}
export function computeContourLength(twoDimRep) {
    const [innerLength, outerLength,] = computeInnerOuterContourLengths(twoDimRep);
    return innerLength + outerLength;
}
export function computeContourCount(twoDimRep) {
    const iops = wsi4.cam.util.extractInnerOuterPolygons(twoDimRep);
    return iops.reduce((acc, iop) => acc + 1 + wsi4.geo.util.innerPolygons(iop).length, 0);
}
export function fileName(filePath) {
    return filePath.substring(filePath.lastIndexOf("/") + 1);
}
export function renderDefaultPng(assembly) {
    const camera = wsi4.geo.assembly.computeDefaultCamera(assembly);
    return wsi4.geo.assembly.renderIntoPng(assembly, camera);
}
export function asyncRenderDefaultPng(assembly) {
    const camera = wsi4.geo.assembly.computeDefaultCamera(assembly);
    return wsi4.geo.assembly.asyncRenderIntoPng(assembly, camera);
}
export function computeSegmentLength(segment) {
    switch (segment.type) {
        case SegmentType.line: {
            return Math.sqrt(Math.pow(segment.content.to.entries[0] - segment.content.from.entries[0], 2) + Math.pow(segment.content.to.entries[1] - segment.content.from.entries[1], 2));
        }
        case SegmentType.arc: {
            return wsi4.throwError("Segment length not implemented for arc segments");
        }
    }
}
function csvDelimiter(locale) {
    const decimalPoint = wsi4.locale.decimalPoint(locale);
    return decimalPoint === "." ? "," : ";";
}
function detectCsvDelimiter(input, locale) {
    const endLineIndex = input.indexOf("\n");
    const rIndex = input.indexOf("\r");
    const firstLineEnd = endLineIndex === -1 && rIndex === -1 ? input.length : (endLineIndex === -1 || rIndex === -1 ? Math.max(endLineIndex, rIndex) : Math.min(endLineIndex, rIndex));
    const firstLine = input.substring(0, firstLineEnd);
    if (firstLine.length <= 0) {
        return csvDelimiter(locale);
    }
    if (firstLine.includes(";")) {
        return ";";
    }
    else if (firstLine.includes(",")) {
        return ",";
    }
    return csvDelimiter(locale);
}
export function parseCsv(input, locale) {
    const delimiter = detectCsvDelimiter(input, locale);
    const rows = wsi4.util.parseCsv(input.trim(), delimiter);
    if (rows === undefined || rows.length === 0) {
        return undefined;
    }
    const keys = rows.shift();
    if (keys === undefined) {
        return undefined;
    }
    if (rows.some(row => row.length !== keys.length)) {
        return undefined;
    }
    return rows.map(row => keys.reduce((acc, key, index) => {
        acc[key] = row[index];
        return acc;
    }, {}));
}
export function toCsvString(rows, locale, precision = 6) {
    return rows.map(row => row.map(value => {
        const str = (() => {
            if (isString(value)) {
                return "\"" + value.replace(/"/g, "\"\"") + "\"";
            }
            else if (isNumber(value)) {
                return wsi4.locale.floatToString(value, locale, precision);
            }
            else if (isBoolean(value)) {
                return value ? "true" : "false";
            }
            else if (value === undefined) {
                return "";
            }
            else {
                wsi4.throwError("Unexpected type for CSV cell");
            }
        })();
        return str;
    })
        .join(csvDelimiter(locale)))
        .join("\r\n");
}
export function lexicographicObjectLess(lhs, rhs) {
    const key = getKeysOfObject(lhs)
        .sort(compareStrings)
        .find(key => lhs[key] !== rhs[key]);
    return key === undefined ? 0 : lhs[key] < rhs[key] ? -1 : 1;
}
export function computeBox3(twoDimRep, thickness) {
    const bbox2 = wsi4.cam.util.boundingBox2(twoDimRep);
    return {
        lower: {
            entries: [
                bbox2.lower.entries[0],
                bbox2.lower.entries[1],
                0.,
            ],
        },
        upper: {
            entries: [
                bbox2.upper.entries[0],
                bbox2.upper.entries[1],
                thickness,
            ],
        },
    };
}
export function assert(condition, msg) {
    if (!condition) {
        wsi4.throwError("Assertion failure" + (msg === undefined ? "" : ": " + msg));
    }
}
export function assertDebug(func, msg) {
    if (wsi4.util.isDebug()) {
        assert(func(), msg);
    }
}
export function recursiveSubAssemblies(assembly) {
    return wsi4.geo.assembly.subAssemblies(assembly)
        .reduce((acc, asm) => {
        acc.push(asm);
        acc.concat(recursiveSubAssemblies(asm));
        return acc;
    }, []);
}
export function computeEntityFaceArea(rootAssembly, entity) {
    switch (entity.descriptor.type) {
        case GeometryEntityType.edge: return 0;
        case GeometryEntityType.face: {
            const assembly = wsi4.geo.assembly.resolvePath(rootAssembly, entity.assemblyPath);
            assert(assembly !== undefined, "Expecting valid assembly path");
            const brep = wsi4.geo.assembly.brep(assembly);
            assert(brep !== undefined, "Expecting valid brep");
            return wsi4.geo.brep.faceArea(brep, entity.descriptor.content.value);
        }
    }
}
export function isElisionMode(arg) {
    const values = {
        none: undefined,
        left: undefined,
        middle: undefined,
        right: undefined,
    };
    return isString(arg) && getKeysOfObject(values)
        .some(key => key === arg);
}
const elideFnMap = {
    none: arg => arg,
    left: (arg, threshold) => arg.substring(arg.length - threshold, arg.length),
    middle: (arg, threshold) => arg.substring(0, Math.ceil(Math.min(arg.length, threshold) / 2)) + arg.substring(arg.length - Math.floor(Math.min(arg.length, threshold) / 2), arg.length),
    right: (arg, threshold) => arg.substring(0, threshold),
};
export function elide(arg, mode, threshold) {
    return elideFnMap[mode](arg, threshold);
}
export function computeNameMaxLengthLowerBound(numNames) {
    return 1 + numNames.toString().length;
}
function makeUnique(nameInput, existingNames, maxLength) {
    const maxLengthLowerBound = computeNameMaxLengthLowerBound(1 + existingNames.length);
    assert(maxLength >= maxLengthLowerBound, "Pre-condition violated:  maxLength must be >= " + maxLengthLowerBound.toString() + "; actual:  " + maxLength.toString());
    const newNameBase = (() => {
        const numberIndexPosition = nameInput.search(/_[0-9]+$/);
        if (numberIndexPosition === -1) {
            return nameInput;
        }
        else {
            return nameInput.substring(0, numberIndexPosition);
        }
    })();
    for (let i = 0; i <= existingNames.length; ++i) {
        const suffix = "_" + i.toFixed(0);
        assert(suffix.length <= maxLength, "Suffix \"" + suffix + "\" exceeds max size of " + maxLength.toString());
        const newName = newNameBase.slice(0, maxLength - suffix.length) + suffix;
        if (existingNames.every(name => name !== newName)) {
            return newName;
        }
    }
    wsi4.throwError("Should not be reached");
}
export function computeUniqueNames(inputNames, existingNamesInput = [], lengthLimitParams = undefined) {
    const [elisionMode, maxLength,] = (() => {
        if (lengthLimitParams === undefined || lengthLimitParams.elisionMode === "none") {
            return [
                "none",
                Number.MAX_SAFE_INTEGER,
            ];
        }
        else {
            const maxLengthLowerBound = computeNameMaxLengthLowerBound(inputNames.length + existingNamesInput.length);
            assert(lengthLimitParams.maxLength >= maxLengthLowerBound, "Pre-condition violated:  maxLength must be >= " + maxLengthLowerBound.toString() + "; actual:  " + lengthLimitParams.maxLength.toString());
            return [
                lengthLimitParams.elisionMode,
                lengthLimitParams.maxLength,
            ];
        }
    })();
    const existingNames = Array.from(existingNamesInput);
    const result = [];
    inputNames.forEach(inputName => {
        const uniqueName = (() => {
            const elidedName = elide(inputName, elisionMode, maxLength);
            if (existingNames.some(n => n === elidedName)) {
                return makeUnique(elidedName, existingNames, maxLength);
            }
            else {
                return elidedName;
            }
        })();
        existingNames.push(uniqueName);
        result.push(uniqueName);
    });
    return result;
}
export function thicknessEquivalence(first, second) {
    return Math.abs(first - second) < tolerances.thickness;
}
export function cleanFileName(input, replacement = "_") {
    const illegalChars = /[/:*?"<>|\n\r\\]/g;
    return input.replace(illegalChars, replacement)
        .trim();
}
export function cleanRelativeDirPath(input, replacement = "_") {
    const illegalChars = /[:*?"<>|\n\r]/g;
    const leadingSlash = /^\//;
    const trailingSlash = /\/$/;
    return input.replace(illegalChars, replacement)
        .replace(leadingSlash, "")
        .replace(trailingSlash, "")
        .trim();
}
export function cleanAbsoluteDirPath(input, replacement = "_") {
    const firstCharIllegalChars = /[:*?"<>|\n\r]/;
    const secondCharIllegalChars = /[*?"<>|\n\r]/;
    const tailIllegalChars = /[:*?"<>|\n\r]/g;
    const trailingSlash = /\/$/;
    const firstChar = input.substring(0, 1);
    const secondChar = input.substring(1, 2);
    const tail = input.substring(2);
    return firstChar.replace(firstCharIllegalChars, replacement) +
        secondChar.replace(secondCharIllegalChars, replacement) +
        tail.replace(tailIllegalChars, replacement)
            .replace(trailingSlash, "")
            .trim();
}
export function computeArrayIntersection(input, equal = isEqual) {
    return input.reduce((acc, arr) => {
        acc.push(...arr);
        return acc;
    }, [])
        .filter((lhs, index, self) => index === self.findIndex(rhs => equal(lhs, rhs)) && input.every(arr => arr.some(rhs => equal(lhs, rhs))));
}
export function exhaustiveStringTuple() {
    return (...x) => x;
}
export function isInstanceOf(arg, typeGuardMap) {
    return isStringIndexedInterface(arg) && getKeysOfObject(typeGuardMap)
        .every(key => typeGuardMap[key](arg[key]));
}
